import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import './Dashboard.css';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Check, Clock, MessageSquare, PanelLeft, RefreshCcw, Sparkle, Sparkles, WalletCards, Maximize2, Minimize2, Folder, Hamburger, Menu, X, Plus, Paperclip, Copy, ThumbsUp, ThumbsDown, RefreshCw, ChevronDown, ArrowUp } from 'lucide-react';
import { useMembership } from '../hooks/useMembership';
import { useSubscription } from '../hooks/useSubscription';
import { database, supabase } from '../supabase';
import PricingModal from './PricingModal';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useNavigate } from 'react-router-dom';
import GoogleDocThumbnail from './GoogleDocThumbnail';
import FileUpload from './FileUpload';
import UpgradeModal from './UpgradeModal';
import FollowUpQuestions from './FollowUpQuestions';
import SettingsModal from './SettingsModal';
import { SHOW_QUIZ_FEATURES } from '../config/features';
import folderArtGreen from '../assets/ChatGPT Image Apr 23, 2026, 02_45_19 PM.png';
import folderArtBlue from '../assets/ChatGPT Image Apr 23, 2026, 02_42_32 PM.png';
import folderArtOrange from '../assets/ChatGPT Image Apr 23, 2026, 02_43_55 PM.png';

const FOLDER_TILE_ART = [folderArtGreen, folderArtBlue, folderArtOrange];

const extractYoutubeVideoId = (url) => {
    if (!url || typeof url !== 'string') return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2] && match[2].length === 11 ? match[2] : null;
};

const getSummarySourceDocumentName = (note) => {
    const s = note?.summary;
    if (s && typeof s === 'object' && !Array.isArray(s)) {
        return s.source_document_name || s._rag_study_source_document_name || null;
    }
    return null;
};

const buildDocumentNameToNoteMap = (notes) => {
    const map = new Map();
    if (!Array.isArray(notes)) return map;
    for (const note of notes) {
        const titleKey = (note.title || '').trim().toLowerCase();
        if (titleKey && !map.has(titleKey)) map.set(titleKey, note);
        const src = getSummarySourceDocumentName(note);
        if (src) {
            const k = String(src).trim().toLowerCase();
            if (k && !map.has(k)) map.set(k, note);
        }
    }
    return map;
};

const buildNotePreview = (note) => {
    const s = note?.summary;
    if (!s) return 'Open to view notes and transcript.';
    if (typeof s === 'string') return s.length > 220 ? `${s.slice(0, 220)}…` : s;
    if (typeof s === 'object') {
        const text =
            s.text ||
            s.overview ||
            s.description ||
            s.summary ||
            (Array.isArray(s.key_points) ? s.key_points.join(' ') : null) ||
            s.note_text;
        if (typeof text === 'string') return text.length > 240 ? `${text.slice(0, 240)}…` : text;
        try {
            const j = JSON.stringify(s);
            return j.length > 200 ? `${j.slice(0, 200)}…` : j;
        } catch {
            return 'YouTube note';
        }
    }
    return 'YouTube note';
};

const Dashboard = forwardRef(({ toggleSidebar, toggleRightSidebar, onDocumentsUpdate, isRightSidebarCollapsed = true }, ref) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('All');
    const [viewMode, setViewMode] = useState('grid');
    const [file, setFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isDragOverScreen, setIsDragOverScreen] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [youtubeNotes, setYoutubeNotes] = useState([]);
    const [linkableNotes, setLinkableNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // Start as false to not block UI
    const { user } = useAuth(); // Use user from AuthContext
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showImportWebLinkModal, setShowImportWebLinkModal] = useState(false);
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [isUploadingYoutube, setIsUploadingYoutube] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeModalData, setUpgradeModalData] = useState({ feature: '', currentUsage: 0, limit: 0 });

    const handleImportWebLinkClick = () => {
        setShowImportWebLinkModal(true);
    };

    const handleCloseImportWebLinkModal = () => {
        setShowImportWebLinkModal(false);
        setYoutubeUrl('');
        setIsUploadingYoutube(false);
    };

    const showUpgradeModalForFeature = async (feature) => {
        try {
            const remaining = await getRemaining(feature, false);
            const limit = membershipData?.limits[feature] || 0;
            const currentUsage = limit - remaining;
            
            setUpgradeModalData({
                feature,
                currentUsage: Math.max(0, currentUsage),
                limit
            });
            setShowUpgradeModal(true);
        } catch (error) {
            console.error('Error getting usage data:', error);
            // Fallback to basic modal
            setUpgradeModalData({
                feature,
                currentUsage: 0,
                limit: 0
            });
            setShowUpgradeModal(true);
        }
    };
    
    const handleSummarizeLink = async () => {
        if (!youtubeUrl.trim()) {
            alert('Please enter a YouTube URL');
            return;
        }
        
        if (!user?.uid) {
            alert('Please log in to upload videos');
            return;
        }
        
        // Check usage limits before proceeding
        const canUploadYoutube = await canPerformAction('youtubeVideos', false); // Daily limit
        if (!canUploadYoutube) {
            await showUpgradeModalForFeature('youtubeVideos');
            return;
        }
        
        setIsUploadingYoutube(true);
        
        try {
            // First, get the Supabase user ID from Firebase UID
            const { data: supabaseUser, error: userError } = await supabase
                .from('users')
                .select('id')
                .eq('firebase_uid', user.uid)
                .single();
            
            if (userError || !supabaseUser) {
                console.error('Error finding Supabase user:', userError);
                alert('User not found. Please try logging in again.');
                return;
            }
            
            const youtubeDocumentName = `YouTube Video - ${new Date().toLocaleDateString()}`;

            const response = await fetch('https://celebrated-charisma-production-5701.up.railway.app/upload_youtube', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    url: youtubeUrl,
                    user_id: user.uid,
                    document_name: youtubeDocumentName
                })
            });
            
            const data = await response.json();
            
             if (response.ok) {
                 const resolvedDocName = String(
                     data.document_name || data.filename || youtubeDocumentName
                 ).trim();

                 const summaryPayload =
                     data.summary && typeof data.summary === 'object' && !Array.isArray(data.summary)
                         ? { ...data.summary, source_document_name: resolvedDocName }
                         : { source_document_name: resolvedDocName, overview: data.summary };

                 // Save the YouTube video data to user's account
                 const noteData = {
                     type: 'youtube_video',
                     title: data.summary?.title || 'YouTube Video',
                     url: youtubeUrl,
                     summary: summaryPayload,
                     transcript_length: data.transcript_length,
                     chunk_count: data.chunk_count,
                     created_at: new Date().toISOString(),
                     user_id: supabaseUser.id // Use Supabase user ID instead of Firebase UID
                 };
                 
                 // Save to Supabase
                 const { data: savedNote, error: saveError } = await supabase
                     .from('notes')
                     .insert([noteData])
                     .select()
                     .single();
                 
                 if (saveError) {
                     console.error('Error saving note:', saveError);
                     alert('Video processed but failed to save. Please try again.');
                     return;
                 }
                 
                 // Track usage after successful YouTube upload
                 await trackUsage('youtubeVideos', false); // Daily tracking
                 
                 // Close modal and navigate to notes page
                 handleCloseImportWebLinkModal();
                 
                 // Navigate to the notes page with the note ID
                 window.location.href = `/notes/${user.uid}/${savedNote.id}`;
            } else {
                alert(`❌ Error: ${data.detail || 'Failed to upload YouTube video'}`);
            }
        } catch (error) {
            console.error('Error uploading YouTube:', error);
            alert('❌ Error uploading YouTube video. Please try again.');
        } finally {
            setIsUploadingYoutube(false);
        }
    };
    
    // Driver.js tour - Start tour for first-time users
    useEffect(() => {
 
        const hasSeenTour = localStorage.getItem('hasSeenDashboardTour');
        if (user && !hasSeenTour) {
            console.log('✅ Starting tour setup...');
            
            // Start tour after a short delay
            setTimeout(() => {
                console.log('🚀 Initializing Driver.js');
                
                // Check if elements exist
                console.log('.create-card exists:', !!document.querySelector('.create-card'));
                console.log('.section-title exists:', !!document.querySelector('.section-title'));
                console.log('.view-toggles exists:', !!document.querySelector('.view-toggles'));
                console.log('.dashboard-title exists:', !!document.querySelector('.dashboard-title'));

                
                const driverObj = driver({
                    showProgress: true,
                    showButtons: ['next', 'previous', 'close'],
                    steps: [
                        {
                            element: '.create-card',
                            popover: {
                                title: 'Upload Your Documents',
                                description: 'Start by uploading a PDF document here. We support any study material!',
                                side: "bottom",
                                align: 'start'
                            }
                        },
                        {
                            element: '.section-title-dashboard',
                            popover: {
                                title: 'Your Documents',
                                description: 'Here you can see all your study documents organized in one place. Selecting the active document lets you use flashcards, chat, and other tools with that material.',
                                side: "bottom",
                                align: 'start'
                            }
                        },
                        {
                            element: '.view-toggles',
                            popover: {
                                title: 'Change View',
                                description: 'Switch between grid and list view to organize your documents.',
                                side: "bottom",
                                align: 'start'
                            }
                        },
                        {
                            element: '.dashboard-title',
                            popover: {
                                title: 'Welcome to Your Dashboard!',
                                description: 'This is your command center for all your study materials. Upload documents, study with flashcards, and more!',
                                side: "bottom",
                                align: 'start'
                            }
                        },
                    ],
                    onDestroyed: () => {
                        console.log('🎉 Tour completed');
                        localStorage.setItem('hasSeenDashboardTour', 'true');
                    }
                });
                
                console.log('📍 Driver object created:', driverObj);
                driverObj.drive();
                console.log('🎬 Tour started!');
            }, 2000);
        } else {
            console.log('❌ Tour not starting - user or driver missing');
        }
    }, [user]);
   
    const handleSettingsClick = () => {
        // If user is on free plan, show pricing modal
        // If user is on paid plan, show settings modal
        if (subscription && subscription.status !== 'active') {
            setShowPricingModal(true);
        } else {
            // setShowSettingsModal(true);
            setShowPricingModal(true);
        }
    };

    const handleCloseSettingsModal = () => {
        setShowSettingsModal(false);
    };

    const handleClosePricingModal = () => {
        setShowPricingModal(false);
    };

    // Handle action button clicks
    const handleActionButtonClick = (actionType) => {
        const buttons = document.querySelectorAll('.action-button');
        buttons.forEach((btn) => btn.classList.remove('active'));

        if (actionType === 'quiz') {
            if (!SHOW_QUIZ_FEATURES) return;
            const quizBtn = document.querySelector('.quiz-button');
            if (quizBtn) quizBtn.classList.add('active');
        }
    };

    // Handle document deletion (local only - no API call)
    const handleDeleteDocument = async (documentId) => {
        if (!documentId) {
            console.error('No document ID provided for deletion');
            return;
        }

        // Show confirmation dialog
        const confirmed = window.confirm('Are you sure you want to remove this document from your library? This will only remove it from this view - the document will still exist on the server.');
        if (!confirmed) {
            return;
        }

        try {
            console.log('Removing document from view:', documentId);
            
            // Find the document to be deleted
            const deletedDoc = documents.find(doc => 
                (doc.document_id || doc.id) === documentId
            );
            
            if (!deletedDoc) {
                console.error('Document not found in current list');
                alert('Document not found in current list');
                return;
            }
            
            // Remove document from local state only
            setDocuments(prev => prev.filter(doc => 
                (doc.document_id || doc.id) !== documentId
            ));
            
            // If the deleted document was selected, clear selection
            if (currentDocumentId === deletedDoc.document_name) {
                setCurrentDocumentId(null);
            }
            
            // Save deleted documents to localStorage to persist across page refreshes
            const deletedDocsKey = `deleted_docs_${user?.uid}`;
            const existingDeleted = JSON.parse(localStorage.getItem(deletedDocsKey) || '[]');
            const updatedDeleted = [...existingDeleted, documentId];
            localStorage.setItem(deletedDocsKey, JSON.stringify(updatedDeleted));
            
            // Notify parent component
            if (onDocumentsUpdate) {
                onDocumentsUpdate();
            }
            
            console.log('Document removed from view successfully');
            alert('Document removed from your library!');
            
        } catch (error) {
            console.error('Error removing document:', error);
            alert('Error removing document. Please try again.');
        }
    };

    // Utility function to restore deleted documents (for debugging/admin purposes)
    const restoreDeletedDocuments = () => {
        const deletedDocsKey = `deleted_docs_${user?.uid}`;
        localStorage.removeItem(deletedDocsKey);
        console.log('All deleted documents restored');
        // Refresh the documents list
        fetchDocuments();
    };

    // Utility function to clear all deleted documents permanently
    const clearDeletedDocuments = () => {
        const deletedDocsKey = `deleted_docs_${user?.uid}`;
        localStorage.removeItem(deletedDocsKey);
        console.log('Deleted documents list cleared');
    };
    
    
    // Membership hook
    const { 
        membershipData, 
        canPerformAction, 
        trackUsage, 
        getRemaining,
        isFree, 
        isPro, 
        isEnterprise,
        currentTier 
    } = useMembership();

    // Subscription hook
    const { 
        subscription, 
        loading: subscriptionLoading, 
        isActive, 
        isPro: isSubscribedPro, 
        isEnterprise: isSubscribedEnterprise,
        hasAccess 
    } = useSubscription();
    const [chatInput, setChatInput] = useState('');
    const chatAskInFlightRef = useRef(false);
    const [showChat, setShowChat] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [messageTimestamps, setMessageTimestamps] = useState([]);
    const [chatLoading, setChatLoading] = useState(false);
    const [quickChatInput, setQuickChatInput] = useState('');
    const [showPricingModal, setShowPricingModal] = useState(false);
    const [loadingModal, setLoadingModal] = useState(false);
    const [successText, setSuccessText] = useState('File uploaded successfully!');
    const [successTip, setSuccessTip] = useState(false);
    const [currentDocumentId, setCurrentDocumentId] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [timer, setTimer] = useState(false);
    
    // Quiz states
    const [showQuiz, setShowQuiz] = useState(false);
    const [showQuizModal, setShowQuizModal] = useState(false);
    
    const [quizLoading, setQuizLoading] = useState(false);
    const [quizResponse, setQuizResponse] = useState(null);
    const [quizzes, setQuizzes] = useState([]);
    const [isQuizExpanded, setIsQuizExpanded] = useState(false);
    
    // Quiz creation options
    const [quizOptions, setQuizOptions] = useState({
        length: 'medium',
        difficulty: 'intermediate', 
        style: 'engaging',
        questionTypes: ['multiple_choice'],
        questionCount: 5
    });

    // Flashcard states
    const [showFlashcards, setShowFlashcards] = useState(false);
    const [flashcardLoading, setFlashcardLoading] = useState(false);
    const [flashcardResponse, setFlashcardResponse] = useState(null);
    const [flashcards, setFlashcards] = useState([]);
    const [currentFlashcardIndex, setCurrentFlashcardIndex] = useState(0);
    const [showFlashcardAnswer, setShowFlashcardAnswer] = useState(false);
    const [isFlashcardExpanded, setIsFlashcardExpanded] = useState(false);

    // Knowledge profile states
    const [knowledgeProfile, setKnowledgeProfile] = useState(null);
    const [showKnowledgeProfile, setShowKnowledgeProfile] = useState(false);

    // Expose functions to parent component
    useImperativeHandle(ref, () => ({
        toggleFlashcards: () => setShowFlashcards((v) => !v),
        toggleQuiz: SHOW_QUIZ_FEATURES ? () => setShowQuiz((q) => !q) : () => {},
        toggleChat: () => setShowChat((v) => !v),
        getCurrentDocumentId: () => currentDocumentId
    }));

    const studioChatFirstName = useMemo(() => {
        const n = user?.displayName?.trim()?.split(/\s+/)[0];
        if (n) return n;
        const fromEmail = user?.email?.split('@')[0];
        return fromEmail || 'there';
    }, [user]);

    const studioActiveDocLabel = useMemo(() => {
        if (!currentDocumentId) return 'No document selected';
        const d = documents.find((x) => x.document_name === currentDocumentId);
        const raw = d?.document_name || currentDocumentId;
        return raw.length > 34 ? `${raw.slice(0, 32)}…` : raw;
    }, [documents, currentDocumentId]);

    // Load user data when user changes (using AuthContext)
    useEffect(() => {
        if (user) {
            console.log('Dashboard: User authenticated, loading user data');
            // Load data in background without blocking UI
            loadUserData();
        }
    }, [user]);

    // Set loading to false after a short timeout to prevent infinite loading
    // No loading timeout needed - dashboard renders immediately

    // Add global drag and drop event listeners
    useEffect(() => {
        const handleGlobalDragOver = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOverScreen(true);
        };

        const handleGlobalDragLeave = (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Only hide if we're leaving the entire document
            if (!e.relatedTarget) {
                setIsDragOverScreen(false);
            }
        };

        const handleGlobalDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOverScreen(false);

            const files = e.dataTransfer.files;
            if (files && files.length > 0) {
                const droppedFile = files[0];
                
                // Validate file type
                console.log('📁 Dropped file:', droppedFile);
                console.log('📁 File type:', droppedFile.type);
                console.log('📁 File size:', droppedFile.size);
                
                if (droppedFile.type === 'application/pdf') {
                    console.log('✅ Valid PDF file, setting file state');
                    setFile(droppedFile);
                } else {
                    console.log('❌ Invalid file type:', droppedFile.type);
                    alert('Please drop a PDF file only.');
                }
            }
        };

        // Add event listeners to document
        document.addEventListener('dragover', handleGlobalDragOver);
        document.addEventListener('dragleave', handleGlobalDragLeave);
        document.addEventListener('drop', handleGlobalDrop);

        // Cleanup
        return () => {
            document.removeEventListener('dragover', handleGlobalDragOver);
            document.removeEventListener('dragleave', handleGlobalDragLeave);
            document.removeEventListener('drop', handleGlobalDrop);
        };
    }, []);

    const loadUserData = async () => {
        try {
            // Use the Google user's Firebase UID directly
            const firebaseUserId = user?.uid;
            if (firebaseUserId) {
                setCurrentUserId(firebaseUserId);
                // localStorage is already set by AuthContext, but ensure it's current
                localStorage.setItem('rag_user_id', firebaseUserId);
                console.log('Using authenticated Google user ID:', firebaseUserId);
            } else {
                console.error('No authenticated user found');
            }
            
            // Use Promise.allSettled to ensure all promises complete even if some fail
            // Only fetch documents for now to avoid Firebase errors
            const results = await Promise.allSettled([
                fetchDocuments(),
                fetchNotesForMaterials(),
            ]);
            
            // Log any failures but don't throw
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    console.error(`Failed to load data for function ${index}:`, result.reason);
                }
            });
            
        } catch (error) {
            console.error('Error loading user data:', error);
        } finally {
            console.log('Dashboard: Setting loading to false');
            setIsLoading(false);
        }
    };

    // API Functions with FIXED session management

    // 1. Upload PDF - This will set the currentUserId for all future requests
    const uploadFile = async (fileToUpload = null) => {
        console.log('🚀 uploadFile called');
        console.log('📁 File object:', fileToUpload || file);
        console.log('👤 User object:', user);
        
        // Use the passed file or the state file
        const fileToUse = fileToUpload || file;
        
        // Check if file is selected
        if (!fileToUse) {
            console.error('❌ No file selected');
            alert('Please select a file first');
            return;
        }
        
        // Check usage limits before proceeding
        console.log('🔍 Checking document upload limits...');
        const canUpload = await canPerformAction('documentUploads', false); // Daily limit
        console.log('🔍 Can upload result:', canUpload);
        if (!canUpload) {
            console.log('🚫 Upload blocked - showing upgrade modal');
            await showUpgradeModalForFeature('documentUploads');
            return;
        }
        console.log('✅ Upload allowed - proceeding with API call');

        setLoadingModal(true);
        try {
            const formData = new FormData();
            formData.append('file', fileToUse);
            
            // Use the authenticated Google user's Firebase UID
            const currentUserId = user?.uid;
            console.log('🆔 Current user ID:', currentUserId);
            
            if (!currentUserId) {
                console.error('❌ No user ID found');
                alert('Please sign in to upload documents');
                setLoadingModal(false);
                return;
            }
            
            console.log('📤 Uploading to:', `https://celebrated-charisma-production-5701.up.railway.app/upload_pdf?user_id=${encodeURIComponent(currentUserId)}`);
            console.log('📁 File details:', {
                name: fileToUse.name,
                size: fileToUse.size,
                type: fileToUse.type
            });
            
            const response = await fetch('https://celebrated-charisma-production-5701.up.railway.app/upload_pdf?user_id=' + encodeURIComponent(currentUserId), {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });
            
            console.log('📡 Response status:', response.status);
            console.log('📡 Response ok:', response.ok);
            console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));
            
            const data = await response.json();
            console.log('📥 Upload response data:', data);
            
            if (response.ok) {
                // Track usage after successful upload
                await trackUsage('documentUploads', false); // Daily tracking
                
                // CRITICAL: Store the user_id from localStorage for all future requests
                setCurrentUserId(currentUserId);
                
                // Document uploaded successfully to backend
                console.log('Upload response data:', data);
                
                const filename = data.filename || data.document_name || fileToUse?.name || 'Unknown';
                setSuccessText(`File uploaded successfully! Document: ${filename}`);
                setSuccessTip(true);
                setTimeout(() => setSuccessTip(false), 3000);
                setShowChat(true);
                // Reload documents - this will set the currentDocumentId
                await fetchDocuments();
            } else {
                console.log(`Upload failed: ${data.detail || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            console.log('Error uploading file: ' + error.message);
        } finally {
            setLoadingModal(false);
        }
    };

    const fetchNotesForMaterials = async () => {
        if (!user?.uid) {
            setYoutubeNotes([]);
            setLinkableNotes([]);
            return;
        }
        try {
            const supabaseUser = await database.getUserByFirebaseUid(user.uid);
            if (!supabaseUser) {
                setYoutubeNotes([]);
                setLinkableNotes([]);
                return;
            }
            const { data, error } = await supabase
                .from('notes')
                .select('*')
                .eq('user_id', supabaseUser.id)
                .order('created_at', { ascending: false })
                .limit(100);

            if (error) {
                console.error('fetchNotesForMaterials:', error);
                setYoutubeNotes([]);
                setLinkableNotes([]);
                return;
            }
            const rows = data || [];
            setLinkableNotes(rows);
            setYoutubeNotes(rows.filter((n) => n.type === 'youtube_video'));
        } catch (e) {
            console.error('fetchNotesForMaterials:', e);
            setYoutubeNotes([]);
            setLinkableNotes([]);
        }
    };

    const documentNameToNote = useMemo(() => buildDocumentNameToNoteMap(linkableNotes), [linkableNotes]);

    // 2. Get Documents - Use stored user_id if available
    const fetchDocuments = async () => {
        try {
            const userId = localStorage.getItem('rag_user_id');
            if (!userId) {
                console.log('No user ID found, skipping document fetch');
                setDocuments([]);
                if (onDocumentsUpdate) {
                    onDocumentsUpdate([]);
                }
                return;
            }

            // Add timeout to prevent hanging
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

            const response = await fetch(`https://celebrated-charisma-production-5701.up.railway.app/my_documents?user_id=${userId}`, {
                method: 'GET',
                credentials: 'include',
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error('Failed to fetch documents');
            }

            const data = await response.json();
            console.log('Documents:', data);
            
            if (data && Array.isArray(data.documents)) {
                // Filter out deleted documents
                const deletedDocsKey = `deleted_docs_${user?.uid}`;
                const deletedDocs = JSON.parse(localStorage.getItem(deletedDocsKey) || '[]');
                const filteredDocuments = data.documents.filter(doc => 
                    !deletedDocs.includes(doc.document_id || doc.id)
                );
                
                console.log(`Filtered ${data.documents.length} documents, removed ${deletedDocs.length} deleted ones`);
                setDocuments(filteredDocuments);
                
                // Notify parent component about documents update
                if (onDocumentsUpdate) {
                    console.log('Dashboard sending documents to parent:', filteredDocuments);
                    onDocumentsUpdate(filteredDocuments);
                }
                // Always set the first document as current if we have filtered documents
                if (filteredDocuments.length > 0) {
                    // Backend returns document_name, not id
                    setCurrentDocumentId(filteredDocuments[0].document_name);
                    console.log('Set current document ID:', filteredDocuments[0].document_name);
                }
            } else {
                console.warn('API returned unexpected data structure:', data);
                setDocuments([]);
                if (onDocumentsUpdate) {
                    onDocumentsUpdate([]);
                }
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Document fetch timed out');
            } else {
                console.error('Error fetching documents:', error);
            }
            setDocuments([]);
            if (onDocumentsUpdate) {
                onDocumentsUpdate([]);
            }
        }
    };

    // Quick Chat Handler - Opens chat modal with input and triggers response
    const handleQuickChat = async () => {
        const message = quickChatInput.trim();
        if (!message) return;

        setQuickChatInput('');
        setShowChat(true);
        setChatInput('');
        // Send immediately with captured text — do not rely on setChatInput + setTimeout(handleChat),
        // or the first “send” sees a stale empty chatInput and does nothing.
        await submitChatQuestion(message, { appendUserMessage: true });
    };

    // 3. Ask Question - Use stored user_id (appendUserMessage: false for regenerate last reply)
    const submitChatQuestion = async (questionText, { appendUserMessage = true } = {}) => {
        const trimmed = (questionText || '').trim();
        if (!trimmed) return;
        if (chatAskInFlightRef.current) return;
        chatAskInFlightRef.current = true;

        try {
            try {
                console.log('Checking chat limits...');
                const canChat = await canPerformAction('aiResponses', false);
                console.log('Can chat result:', canChat);
                if (!canChat) {
                    await showUpgradeModalForFeature('aiResponses');
                    return;
                }
            } catch (error) {
                console.error('Error checking chat limits:', error);
                console.log('Allowing chat due to limit check error');
            }

            const userTimestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            if (appendUserMessage) {
                setChatMessages((prev) => [...prev, `You: ${trimmed}`]);
                setMessageTimestamps((prev) => [...prev, userTimestamp]);
            }

            setChatLoading(true);
            try {
                const requestBody = {
                    question: trimmed
                };

                if (currentDocumentId) {
                    requestBody.document_name = currentDocumentId;
                }

                const userId = localStorage.getItem('rag_user_id') || currentUserId;
                if (userId) {
                    requestBody.user_id = userId;
                }

                console.log('Sending question with:', requestBody);

                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 30000);

                const response = await fetch('https://celebrated-charisma-production-5701.up.railway.app/ask_question', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify(requestBody),
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                const data = await response.json();
                console.log('Question asked:', data);

                if (response.ok) {
                    trackUsage('aiResponses', true);
                    trackUsage('chatMessages', false);

                    const aiMessage = `AI: ${data.answer}`;
                    const aiTimestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    setChatMessages((prev) => [...prev, aiMessage]);
                    setMessageTimestamps((prev) => [...prev, aiTimestamp]);
                    setSuccessText(`Question answered successfully!`);
                    setSuccessTip(true);
                    setTimeout(() => setSuccessTip(false), 3000);

                    if (window.showFollowUpQuestions) {
                        window.showFollowUpQuestions();
                    }
                } else {
                    console.log(`Error asking question: ${data.detail || 'Unknown error'}`);
                }
            } catch (error) {
                console.error('Error asking question:', error);
                if (error.name === 'AbortError') {
                    console.log('Question timed out. Please try again with a shorter question.');
                } else {
                    console.log('Error asking question: ' + error.message);
                }
            } finally {
                setChatLoading(false);
            }
        } finally {
            chatAskInFlightRef.current = false;
        }
    };

    const handleChat = async () => {
        const q = chatInput.trim();
        if (!q) return;
        setChatInput('');
        await submitChatQuestion(q, { appendUserMessage: true });
    };

    const handleStudioRegenerate = (aiMessageIndex) => {
        let userIdx = -1;
        for (let i = aiMessageIndex - 1; i >= 0; i -= 1) {
            if (chatMessages[i]?.startsWith('You:')) {
                userIdx = i;
                break;
            }
        }
        if (userIdx < 0) return;
        const q = chatMessages[userIdx].replace(/^You:\s*/, '');
        setChatMessages((prev) => prev.slice(0, aiMessageIndex));
        setMessageTimestamps((prev) => prev.slice(0, aiMessageIndex));
        void submitChatQuestion(q, { appendUserMessage: false });
    };

    const copyStudioText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (e) {
            console.error('Copy failed', e);
        }
    };

    // 4. Get Chat History
    const fetchChatHistory = async () => {
        try {
            const url = currentDocumentId 
                ? `https://celebrated-charisma-production-5701.up.railway.app/chat_history?document_name=${currentDocumentId}`
                : 'https://celebrated-charisma-production-5701.up.railway.app/chat_history';
            
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch chat history');
            }
            
            const data = await response.json();
            console.log('Chat history:', data);
            
            if (data && Array.isArray(data.chat_history)) {
                const formattedMessages = data.chat_history.flatMap(chat => [
                    `You: ${chat.question}`,
                    `AI: ${chat.answer}`
                ]);
                setChatMessages(formattedMessages);
                // Generate timestamps for historical messages (using current time as placeholder)
                const timestamps = formattedMessages.map(() => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
                setMessageTimestamps(timestamps);
            }
        } catch (error) {
            console.error('Error fetching chat history:', error);
        }
    };

    // 5. Generate Quiz
    const generateQuiz = async () => {
        if (!SHOW_QUIZ_FEATURES) return;
        if (!currentDocumentId) {
            console.log('Please upload a document first. Document ID not set.');
            return;
        }

        // Check usage limits before proceeding
        const canGenerateQuiz = await canPerformAction('aiResponses', false); // Daily limit
        if (!canGenerateQuiz) {
            await showUpgradeModalForFeature('aiResponses');
            return;
        }

        setQuizLoading(true);
        setQuizResponse(null); // Clear previous quiz
        setShowQuiz(true); // Show quiz modal immediately with loading state
        
        try {
            const requestBody = {
                document_name: currentDocumentId,
                question_count: quizOptions.questionCount,
                difficulty: quizOptions.difficulty,
                style: quizOptions.style,
                question_types: quizOptions.questionTypes,
                length: quizOptions.length
            };
            
            // Add user_id if we have one
            const userId = localStorage.getItem('rag_user_id') || currentUserId;
            if (userId) {
                requestBody.user_id = userId;
            }
            
            console.log('Generating quiz with request:', requestBody);
            
            // Add timeout to prevent hanging requests
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
            
            const response = await fetch('https://celebrated-charisma-production-5701.up.railway.app/generate_quiz', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(requestBody),
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            const data = await response.json();
            console.log('Quiz generated response:', data);
            
            if (response.ok) {
                // Parse the quiz content properly
                let parsedQuestions = [];
                
                if (data.quiz) {
                    const rawContent = data.quiz;
                    console.log('=== RAW QUIZ CONTENT DEBUG ===');
                    console.log('Full content:', rawContent);
                    console.log('Content length:', rawContent.length);
                    console.log('First 1000 chars:', rawContent.substring(0, 1000));
                    console.log('=== END DEBUG ===');
                    
                    // Try multiple patterns to handle different quiz formats from the AI
                    let matches = [];
                    
                    // Pattern 1: Standard format with **Question X:** and **Correct Answer:** **Explanation:**
                    let questionPattern = /\*\*Question\s*(\d+):\*\*\s*(.*?)\s*A\)\s*(.*?)\s*B\)\s*(.*?)\s*C\)\s*(.*?)\s*D\)\s*(.*?)\s*\*\*Correct Answer:\*\*\s*[A-D]\)\s*(.*?)\s*\*\*Explanation:\*\*\s*(.*?)(?=\*\*Question\s*\d+:|$)/gis;
                    matches = [...rawContent.matchAll(questionPattern)];
                    
                    // Pattern 2: Format with numbered questions and **Correct Answer:** **Explanation:**
                    if (matches.length === 0) {
                        questionPattern = /(\d+)\.\s*\*\*(.*?)\*\*\s*A\)\s*(.*?)\s*B\)\s*(.*?)\s*C\)\s*(.*?)\s*D\)\s*(.*?)\s*\*\*Correct Answer:\*\*\s*[A-D]\)\s*(.*?)\s*\*\*Explanation:\*\*\s*(.*?)(?=\d+\.|$)/gis;
                        matches = [...rawContent.matchAll(questionPattern)];
                    }
                    
                    // Pattern 3: Format with **CORRECT ANSWER:** and **EXPLANATION:**
                    if (matches.length === 0) {
                        questionPattern = /(\d+)\.\s*\*\*(.*?)\*\*\s*A\)\s*(.*?)\s*B\)\s*(.*?)\s*C\)\s*(.*?)\s*D\)\s*(.*?)\s*\*\*CORRECT ANSWER:\*\*\s*[A-D]\)\s*(.*?)\s*\*\*EXPLANATION:\*\*\s*(.*?)(?=\d+\.|$)/gis;
                        matches = [...rawContent.matchAll(questionPattern)];
                    }
                    
                    // Pattern 4: Format with **🔍 Correct Answer:** and **📝 Explanation:**
                    if (matches.length === 0) {
                        questionPattern = /\*\*Question\s*(\d+):\*\*\s*(.*?)\s*A\)\s*(.*?)\s*B\)\s*(.*?)\s*C\)\s*(.*?)\s*D\)\s*(.*?)\s*\*\*🔍 Correct Answer:\*\*\s*[A-D]\)\s*(.*?)\s*\*\*📝 Explanation:\*\*\s*(.*?)(?=\*\*Question\s*\d+:|$)/gis;
                        matches = [...rawContent.matchAll(questionPattern)];
                    }
                    
                    // Pattern 5: Simple format without bold formatting
                    if (matches.length === 0) {
                        questionPattern = /(\d+)\.\s*(.*?)\s*A\)\s*(.*?)\s*B\)\s*(.*?)\s*C\)\s*(.*?)\s*D\)\s*(.*?)\s*Correct Answer:\s*[A-D]\)\s*(.*?)\s*Explanation:\s*(.*?)(?=\d+\.|$)/gis;
                        matches = [...rawContent.matchAll(questionPattern)];
                    }
                    
                    // Pattern 6: Very simple format - just look for numbered questions with A) B) C) D)
                    if (matches.length === 0) {
                        questionPattern = /(\d+)\.\s*(.*?)\s*A\)\s*(.*?)\s*B\)\s*(.*?)\s*C\)\s*(.*?)\s*D\)\s*(.*?)(?=\d+\.|$)/gis;
                        matches = [...rawContent.matchAll(questionPattern)];
                        console.log('Pattern 6 (simple) found matches:', matches.length);
                    }
                    
                    console.log('Parsing matches found:', matches.length);
                    
                    if (matches.length > 0) {
                        console.log('✅ PARSING SUCCESS! Using pattern that found', matches.length, 'matches');
                        parsedQuestions = matches.map((match, index) => {
                            // Determine which pattern matched and extract accordingly
                            let questionText, optionA, optionB, optionC, optionD, explanation;
                            
                            // Pattern 1 & 4: **Question X:** format - groups: 1=number, 2=question, 3-6=options, 7=answer, 8=explanation
                            if (match[0].includes('**Question') || match[0].includes('🔍')) {
                                questionText = match[2]?.trim();
                                optionA = match[3]?.trim();
                                optionB = match[4]?.trim();
                                optionC = match[5]?.trim();
                                optionD = match[6]?.trim();
                                explanation = match[8]?.trim();
                            }
                            // Pattern 2, 3, 5: Standard numbered format - groups: 1=number, 2=question, 3-6=options, 7=answer, 8=explanation
                            else {
                                questionText = match[2]?.trim();
                                optionA = match[3]?.trim();
                                optionB = match[4]?.trim();
                                optionC = match[5]?.trim();
                                optionD = match[6]?.trim();
                                explanation = match[8]?.trim();
                            }
                            
                            // Clean the options to remove any explanation text that might have leaked in
                            const cleanOptions = [
                                cleanOptionText(optionA),
                                cleanOptionText(optionB),
                                cleanOptionText(optionC),
                                cleanOptionText(optionD)
                            ].filter(Boolean);
                            
                            // Get the explanation from the parsed content
                            let finalExplanation = "Generated from document content";
                            if (explanation && explanation.trim()) {
                                finalExplanation = explanation.trim();
                            }
                            
                            return {
                            id: `generated_${index}`,
                                question: cleanQuestionText(questionText),
                                options: cleanOptions,
                            correct_answer: 0,
                                explanation: finalExplanation
                            };
                        });

                        // Supabase quiz save function
                        const saveQuiz = async () => {
                            try {
                                // Get user from Firebase Auth
                                if (!user) {
                                    console.log('No authenticated user found, skipping save');
                                    return;
                                }

                                // Get Supabase user ID
                                const supabaseUser = await database.getUserByFirebaseUid(user.uid);
                                if (!supabaseUser) {
                                    console.log('User not found in Supabase, skipping save');
                                    return;
                                }
                                
                                const quizData = {
                                    document_name: currentDocumentId,
                                    quiz: parsedQuestions,
                                    question_count: parsedQuestions.length
                                };
                                
                                console.log('💾 Saving quiz to Supabase...');
                                console.log('Quiz data:', quizData);
                                
                                // Save to Supabase
                                const savedQuiz = await database.saveQuiz(supabaseUser.id, quizData);
                                console.log('✅ Quiz saved successfully to Supabase with ID:', savedQuiz.id);
                                console.log('Quiz saved to Supabase with ID: ' + savedQuiz.id);
                                
                                setQuizResponse(prev => ({
                                    ...prev,
                                    quiz_id: savedQuiz.id
                                }));
                            } catch (error) {
                                console.error('❌ Failed to save quiz to Supabase:', error);
                                console.log('Error saving quiz: ' + error.message);
                            }
                        };

                        console.log('🚀 CALLING simple saveQuiz() function...');
                        saveQuiz();
                    } else {
                        console.log('❌ PARSING FAILED - Using fallback quiz structure');
                        // Fallback: create a single question with the raw content
                        parsedQuestions = [{
                            id: 'generated_0',
                            question: 'Generated Quiz Question',
                            options: ['View generated content below'],
                            correct_answer: 0,
                            explanation: rawContent
                        }];
                    }
                }
                
                const quizResponse = {
                    questions: parsedQuestions,
                    quiz_id: data.quiz_id || `quiz_${Date.now()}`,
                    document_name: data.document_name || currentDocumentId
                };
                
                // Track usage after successful quiz generation (non-blocking)
                trackUsage('aiResponses', true); // Monthly tracking
                trackUsage('quizGenerations', true); // Monthly tracking
                
                
                console.log('Setting quiz response:', quizResponse);
                setQuizResponse(quizResponse);
                setSuccessText(`Quiz generated successfully! ${parsedQuestions.length} questions created.`);
                setSuccessTip(true);
                setTimeout(() => setSuccessTip(false), 3000);
                
                // Reload quizzes to update the list (non-blocking)
                fetchQuizzes().catch(err => console.error('Error fetching quizzes:', err));
            } else {
                console.error('Quiz generation failed:', data);
                console.log(`Error generating quiz: ${data.detail || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error generating quiz:', error);
            if (error.name === 'AbortError') {
                console.log('Quiz generation timed out. Please try again with a smaller document or fewer questions.');
            } else {
                console.log('Error generating quiz: ' + error.message);
            }
        } finally {
            setQuizLoading(false);
        }
    };

    // 6. Get Quizzes
    const fetchQuizzes = async () => {
        if (!SHOW_QUIZ_FEATURES) {
            setQuizzes([]);
            return;
        }
        try {
            const userId = localStorage.getItem('rag_user_id');
            const response = await fetch(`https://celebrated-charisma-production-5701.up.railway.app/my_quizzes?user_id=${userId}`, {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch quizzes');
            }
            
            const data = await response.json();
            console.log('Raw quizzes data:', data);
            
            if (data && Array.isArray(data.quizzes)) {
                // Parse the raw quiz text into structured questions
                const parsedQuizzes = [];
                
                data.quizzes.forEach(quizSet => {
                    if (quizSet.quiz) {
                        // Parse the raw quiz content
                        const rawContent = quizSet.quiz;
                        
                        // Try multiple parsing patterns for different quiz formats
                        console.log('Raw quiz content to parse:', rawContent);
                        
                        // Pattern 1: Numbered questions with A), B), C), D) options
                        let questionPattern = /(\d+)\.\s*(.*?)\s*A\)\s*(.*?)\s*B\)\s*(.*?)\s*C\)\s*(.*?)\s*D\)\s*(.*?)(?=\d+\.|$)/gis;
                        let matches = [...rawContent.matchAll(questionPattern)];
                        
                        // Pattern 2: If first pattern fails, try with different spacing
                        if (matches.length === 0) {
                            questionPattern = /(\d+)\.\s*(.*?)\s*A\)\s*(.*?)\s*B\)\s*(.*?)\s*C\)\s*(.*?)\s*D\)\s*(.*?)/gis;
                            matches = [...rawContent.matchAll(questionPattern)];
                        }
                        
                        // Pattern 3: Try with different line break handling
                        if (matches.length === 0) {
                            questionPattern = /(\d+)\.\s*(.*?)\n\s*A\)\s*(.*?)\n\s*B\)\s*(.*?)\n\s*C\)\s*(.*?)\n\s*D\)\s*(.*?)(?=\n\s*\d+\.|$)/gis;
                            matches = [...rawContent.matchAll(questionPattern)];
                        }
                        
                        console.log('Parsing matches found:', matches.length);
                        
                        if (matches.length > 0) {
                            const questions = matches.map((match, index) => ({
                                id: `${quizSet.id}_${index}`,
                                question: cleanQuestionText(match[2]?.trim()),
                                options: [
                                    cleanOptionText(match[3]?.trim()),
                                    cleanOptionText(match[4]?.trim()),
                                    cleanOptionText(match[5]?.trim()),
                                    cleanOptionText(match[6]?.trim())
                                ].filter(Boolean),
                                correct_answer: 0, // We'll need to parse this from the AI response
                                explanation: "Generated from document content"
                            }));
                            
                            parsedQuizzes.push({
                                id: quizSet.id,
                                questions: questions,
                                document_name: quizSet.document_name,
                                created_at: quizSet.created_at
                            });
                        } else {
                            // If parsing fails, create a simple question from the raw content
                            console.log('Parsing failed, creating fallback quiz structure');
                            parsedQuizzes.push({
                                id: quizSet.id,
                                questions: [{
                                    id: `${quizSet.id}_fallback`,
                                    question: "Generated Quiz Content",
                                    options: ["View raw content below"],
                                    correct_answer: 0,
                                    explanation: rawContent,
                                    is_raw_content: true
                                }],
                                document_name: quizSet.document_name,
                                created_at: quizSet.created_at,
                                raw_content: rawContent
                            });
                        }
                    }
                });
                
                console.log('Parsed quizzes:', parsedQuizzes);
                setQuizzes(parsedQuizzes);
            }
        } catch (error) {
            console.error('Error fetching quizzes:', error);
        }
    };

    // Helper function to clean flashcard text
    const cleanFlashcardText = (text) => {
        return text
            .replace(/^Flashcard \d+:\s*/g, '')
            .replace(/^\d+\.\s*\*\*Flashcard \d+\*\*\s*/g, '')
            .replace(/^\*\*Front:\*\*\s*/g, '')  // Remove **Front:**
            .replace(/^\*\*Back:\*\*\s*/g, '')   // Remove **Back:**
            .replace(/^Front:\s*/g, '')
            .replace(/^Back:\s*/g, '')
            .replace(/^Question:\s*/g, '')
            .replace(/^Answer:\s*/g, '')
            .replace(/^Q:\s*/g, '')
            .replace(/^A:\s*/g, '')
            .replace(/^\d+\.\s*/g, '')
            .replace(/\*\*Flashcard \d+\*\*\s*/g, '')
            .replace(/^-\s*\*\*Back:\*\*\s*/g, '')  // Remove - **Back:**
            .replace(/^-\s*\*\*Front:\*\*\s*/g, '') // Remove - **Front:**
            .replace(/^-\s*Front:\s*/g, '')         // Remove - Front:
            .replace(/^-\s*Back:\s*/g, '')          // Remove - Back:
            .trim();
    };

    // Helper function to clean question text
    const cleanQuestionText = (text) => {
        return text
            .replace(/^\*\*True or False:\*\*\s*/gi, '')
            .replace(/^\*\*True\/False:\*\*\s*/gi, '')
            .replace(/^\*\*Multiple Choice:\*\*\s*/gi, '')
            .replace(/^\*\*Fill in the Blank:\*\*\s*/gi, '')
            .replace(/^\*\*Fill in the Blanks:\*\*\s*/gi, '')
            .replace(/^\*\*Match Pairs:\*\*\s*/gi, '')
            .replace(/^\*\*Matching:\*\*\s*/gi, '')
            .replace(/^True or False:\s*/gi, '')
            .replace(/^True\/False:\s*/gi, '')
            .replace(/^Multiple Choice:\s*/gi, '')
            .replace(/^Fill in the Blank:\s*/gi, '')
            .replace(/^Fill in the Blanks:\s*/gi, '')
            .replace(/^Match Pairs:\s*/gi, '')
            .replace(/^Matching:\s*/gi, '')
            .replace(/\*\*/g, '') // Remove all asterisks
            .trim();
    };

    // Helper function to clean raw quiz content (remove correct answers and explanations)
    const cleanRawQuizContent = (text) => {
        return text
            .replace(/\*\*Correct Answer:\*\*.*?(?=\*\*Explanation:\*\*|$)/gis, '') // Remove correct answer sections
            .replace(/\*\*Explanation:\*\*.*?(?=\d+\.|$)/gis, '') // Remove explanation sections
            .replace(/\*\*Correct Answer:\*\*.*$/gis, '') // Remove any remaining correct answer
            .replace(/\*\*Explanation:\*\*.*$/gis, '') // Remove any remaining explanation
            .replace(/\*\*/g, '') // Remove all asterisks
            .replace(/\n\s*\n/g, '\n') // Clean up extra line breaks
            .trim();
    };

    // Helper function to clean option text (remove correct answers and explanations from options)
    const cleanOptionText = (text) => {
        if (!text) return '';
        
        return text
            // Remove correct answer patterns
            .replace(/\*\*Correct Answer:\*\*.*$/gis, '')
            .replace(/Correct Answer:\s*[A-D]\)\s*.*$/gis, '')
            .replace(/Correct Answer:.*$/gis, '')
            
            // Remove explanation patterns
            .replace(/\*\*Explanation:\*\*.*$/gis, '')
            .replace(/Explanation:\s*.*$/gis, '')
            
            // Remove any remaining explanation text that might have leaked in
            .replace(/\s*Explanation:.*$/gis, '')
            .replace(/\s*This explanation.*$/gis, '')
            .replace(/\s*Deion's.*$/gis, '') // Remove context that might be explanation
            
            // Clean up formatting
            .replace(/\*\*/g, '') // Remove all asterisks
            .replace(/\n/g, ' ') // Replace newlines with spaces
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .trim();
    };

    // 7. Generate Flashcards
    const generateFlashcards = async () => {
        console.log('Generate flashcards clicked, currentDocumentId:', currentDocumentId);
        
        if (!currentDocumentId) {
            alert('Please upload a document first. Document ID not set.');
            return;
        }

        // Check usage limits before proceeding
        const canGenerateFlashcards = await canPerformAction('aiResponses', false); // Daily limit
        if (!canGenerateFlashcards) {
            await showUpgradeModalForFeature('aiResponses');
            return;
        }

        setFlashcardLoading(true);
        setFlashcardResponse(null); // Clear previous flashcards
        setShowFlashcards(true); // Show flashcard modal immediately with loading state
        
        try {
            // Get user_id from localStorage (consistent with other functions)
            const userId = localStorage.getItem('rag_user_id') || currentUserId;
            
            const requestBody = {
                user_id: userId,
                document_name: currentDocumentId,  // Backend expects document_name
                count: 10
            };
            
            console.log('Generating flashcards with request:', requestBody);
            
            // Add timeout to prevent hanging requests
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
            
            const response = await fetch('https://celebrated-charisma-production-5701.up.railway.app/generate_flashcards', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(requestBody),
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            const data = await response.json();
            console.log('Flashcards generated response:', data);
            console.log('Data type:', typeof data);
            console.log('Data.flashcards type:', typeof data.flashcards);
            console.log('Data.flashcards content:', data.flashcards);
            
            if (response.ok) {
                // Parse the flashcard content properly
                let parsedFlashcards = [];
                
                if (data.flashcards) {
                    const rawContent = data.flashcards;
                    console.log('Raw flashcard content:', rawContent);
                    console.log('Raw flashcard type:', typeof rawContent);
                    console.log('Raw flashcard length:', rawContent?.length);
                    
                    // Try to parse the flashcard content - handle both string and array formats
                    if (Array.isArray(rawContent)) {
                        // If it's already an array of flashcards
                        parsedFlashcards = rawContent.map((card, index) => ({
                            id: `generated_${index}`,
                            front: cleanFlashcardText(card.front || card.question || 'Front'),
                            back: cleanFlashcardText(card.back || card.answer || 'Back')
                        }));
                    } else if (typeof rawContent === 'string') {
                        // Try to parse string format
                        const flashcardPattern = /(\d+)\.\s*(?:\*\*)?Front:\s*(.*?)\s*(?:\*\*)?Back:\s*(.*?)(?=\d+\.|$)/gis;
                        const matches = [...rawContent.matchAll(flashcardPattern)];
                        
                        if (matches.length > 0) {
                            parsedFlashcards = matches.map((match, index) => ({
                                id: `generated_${index}`,
                                front: cleanFlashcardText(match[2]?.trim()),
                                back: cleanFlashcardText(match[3]?.trim())
                            }));
                        } else {
                            // Try pattern for "1. **Flashcard 1** - Front: ... - Back: ..." format
                            const flashcardWithNumberPattern = /(\d+)\.\s*\*\*Flashcard \d+\*\*\s*-\s*(?:\*\*)?Front:\s*(.*?)\s*-\s*(?:\*\*)?Back:\s*(.*?)(?=\d+\.|$)/gis;
                            const flashcardWithNumberMatches = [...rawContent.matchAll(flashcardWithNumberPattern)];
                            
                            if (flashcardWithNumberMatches.length > 0) {
                                parsedFlashcards = flashcardWithNumberMatches.map((match, index) => ({
                                    id: `generated_${index}`,
                                    front: cleanFlashcardText(match[2]?.trim()),
                                    back: cleanFlashcardText(match[3]?.trim())
                                }));
                            } else {
                                // Try alternative pattern for "Flashcard:" format
                                const altPattern = /Flashcard:\s*(?:\*\*)?Front:\s*(.*?)\s*(?:\*\*)?Back:\s*(.*?)(?=Flashcard:|$)/gis;
                                const altMatches = [...rawContent.matchAll(altPattern)];
                                
                                if (altMatches.length > 0) {
                                    parsedFlashcards = altMatches.map((match, index) => ({
                                        id: `generated_${index}`,
                                        front: cleanFlashcardText(match[1]?.trim()),
                                        back: cleanFlashcardText(match[2]?.trim())
                                    }));
                                } else {
                                    // Fallback: try to extract questions and answers from raw content
                                    const lines = rawContent.split('\n').filter(line => line.trim());
                                    parsedFlashcards = [];
                                    
                                    for (let i = 0; i < lines.length; i += 2) {
                                        if (i + 1 < lines.length) {
                                            parsedFlashcards.push({
                                                id: `generated_${parsedFlashcards.length}`,
                                                front: cleanFlashcardText(lines[i]),
                                                back: cleanFlashcardText(lines[i + 1])
                                            });
                                        }
                                        }
                                    
                                    // If still no flashcards, try one more pattern for simple numbered list
                                    if (parsedFlashcards.length === 0) {
                                        // Try to split by lines and pair them up
                                        const lines = rawContent.split('\n').filter(line => line.trim());
                                        const pairs = [];
                                        
                                        for (let i = 0; i < lines.length; i += 2) {
                                            if (i + 1 < lines.length) {
                                                pairs.push({
                                                    front: cleanFlashcardText(lines[i]),
                                                    back: cleanFlashcardText(lines[i + 1])
                                                });
                                            }
                                        }
                                        
                                        if (pairs.length > 0) {
                                            parsedFlashcards = pairs.map((pair, index) => ({
                                                id: `generated_${index}`,
                                                front: pair.front,
                                                back: pair.back
                                            }));
                                        } else {
                                            // Last resort - create from raw content
                                            const cleanContent = cleanFlashcardText(rawContent);
                                            parsedFlashcards = [{
                                                id: 'generated_0',
                                                front: cleanContent.substring(0, 100) + (cleanContent.length > 100 ? '...' : ''),
                                                back: 'Please try generating flashcards again or contact support.'
                                            }];
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                
                const flashcardResponse = {
                    flashcards: parsedFlashcards,
                    flashcard_id: data.flashcard_id || `flashcard_${Date.now()}`,
                    document_name: data.document_name || currentDocumentId
                };
                
                // Track usage after successful flashcard generation (non-blocking)
                trackUsage('aiResponses', true); // Monthly tracking
                trackUsage('flashcardGenerations', true); // Monthly tracking
                
                // Filter out invalid flashcards (empty front or back)
                const validFlashcards = parsedFlashcards.filter(card => 
                    card.front && card.back && 
                    card.front.trim() !== '' && card.back.trim() !== '' &&
                    !card.front.includes('**Flashcard') && !card.back.includes('**Flashcard')
                );
                
                console.log('Valid flashcards:', validFlashcards);
                console.log('Setting flashcard response:', flashcardResponse);
                
                if (validFlashcards.length > 0) {
                    const cleanResponse = {
                        ...flashcardResponse,
                        flashcards: validFlashcards
                    };
                    setFlashcardResponse(cleanResponse);
                    setFlashcards(validFlashcards);
                    setCurrentFlashcardIndex(0);
                    setShowFlashcardAnswer(false);
                    
                    // Save flashcards to Supabase (non-blocking)
                    const saveFlashcards = async () => {
                        try {
                            // Get user from Firebase Auth
                            if (!user) {
                                console.log('No authenticated user found, skipping flashcard save');
                                return;
                            }

                            // Get Supabase user ID
                            const supabaseUser = await database.getUserByFirebaseUid(user.uid);
                            if (!supabaseUser) {
                                console.log('User not found in Supabase, skipping flashcard save');
                                return;
                            }
                            
                            const flashcardData = {
                                document_name: currentDocumentId,
                                flashcards: validFlashcards
                            };
                            
                            console.log('💾 Saving flashcards to Supabase...');
                            const savedFlashcards = await database.saveFlashcards(supabaseUser.id, flashcardData);
                            console.log('✅ Flashcards saved successfully to Supabase with ID:', savedFlashcards.id);
                            
                        } catch (error) {
                            console.error('❌ Failed to save flashcards to Supabase:', error);
                        }
                    };
                    
                    // Call save function (non-blocking)
                    saveFlashcards();
                    
                    setSuccessText(`Flashcards generated successfully! ${validFlashcards.length} cards created.`);
                    setSuccessTip(true);
                    setTimeout(() => setSuccessTip(false), 3000);
                } else {
                    console.error('No valid flashcards found after parsing');
                    alert('No valid flashcards could be generated. Please try again or contact support.');
                    setSuccessText('Failed to generate valid flashcards. Please try again.');
                    setSuccessTip(true);
                    setTimeout(() => setSuccessTip(false), 3000);
                }
                
                // Reload flashcards to update the list (non-blocking)
                fetchFlashcards().catch(err => console.error('Error fetching flashcards:', err));
            } else {
                console.error('Flashcard generation failed:', data);
                console.log(`Error generating flashcards: ${data.detail || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error generating flashcards:', error);
            if (error.name === 'AbortError') {
                console.log('Flashcard generation timed out. Please try again with a smaller document.');
            } else {
                console.log('Error generating flashcards: ' + error.message);
            }
        } finally {
            setFlashcardLoading(false);
        }
    };

    // 8. Get Flashcards
    const fetchFlashcards = async () => {
        try {
            const userId = localStorage.getItem('rag_user_id');
            const url = currentDocumentId 
                ? `https://celebrated-charisma-production-5701.up.railway.app/my_flashcards?user_id=${userId}&document_name=${currentDocumentId}`
                : `https://celebrated-charisma-production-5701.up.railway.app/my_flashcards?user_id=${userId}`;
            
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch flashcards');
            }
            
            const data = await response.json();
            console.log('Raw flashcards data:', data);
            
            if (data && Array.isArray(data.flashcards)) {
                // Parse the raw flashcard text into individual flashcards
                const parsedFlashcards = [];
                
                data.flashcards.forEach(flashcardSet => {
                    if (flashcardSet.flashcards) {
                        // Parse the raw text content
                        const rawContent = flashcardSet.flashcards;
                        
                        // Parse the numbered flashcard format from your console log
                        const cardPattern = /(\d+)\.\s*(?:\*\*)?Front:\s*(.*?)\s*(?:\*\*)?Back:\s*(.*?)(?=\d+\.\s*(?:\*\*)?Front:|$)/gs;
                        const matches = [...rawContent.matchAll(cardPattern)];
                        
                        let cards = [];
                        
                        if (matches.length > 0) {
                            cards = matches.map((match, index) => ({
                                id: `${flashcardSet.id}_${index}`,
                                front: match[2]?.trim() || 'Front',
                                back: match[3]?.trim() || 'Back',
                                document_name: flashcardSet.document_name
                            }));
                        } else {
                            // Fallback: try other patterns
                            const fallbackPatterns = [
                                /(?:\*\*)?Front:\s*(.*?)\s*(?:\*\*)?Back:\s*(.*?)(?=(?:\*\*)?Front:|$)/gs,
                                /Q:\s*(.*?)\s*A:\s*(.*?)(?=Q:|$)/gs,
                                /Question:\s*(.*?)\s*Answer:\s*(.*?)(?=Question:|$)/gs
                            ];
                            
                            for (const pattern of fallbackPatterns) {
                                const fallbackMatches = [...rawContent.matchAll(pattern)];
                                if (fallbackMatches.length > 0) {
                                    cards = fallbackMatches.map((match, index) => ({
                                        id: `${flashcardSet.id}_${index}`,
                                        front: match[1]?.trim() || 'Front',
                                        back: match[2]?.trim() || 'Back',
                                        document_name: flashcardSet.document_name
                                    }));
                                    break;
                                }
                            }
                        }
                        
                        // If no pattern matched, create a single card with the raw content
                        if (cards.length === 0) {
                            cards = [{
                                id: flashcardSet.id,
                                front: 'Flashcard Set',
                                back: rawContent.substring(0, 500) + (rawContent.length > 500 ? '...' : ''),
                                document_name: flashcardSet.document_name
                            }];
                        }
                        
                        parsedFlashcards.push(...cards);
                    }
                });
                
                console.log('Parsed flashcards:', parsedFlashcards);
                setFlashcards(parsedFlashcards);
            }
        } catch (error) {
            console.error('Error fetching flashcards:', error);
        }
    };

    // 9. Review Flashcard
    const reviewFlashcard = async (flashcardId, difficulty) => {
        try {
            const requestBody = {
                flashcard_id: flashcardId,
                difficulty: difficulty
            };
            
            // Add user_id if we have one
            if (currentUserId) {
                requestBody.user_id = currentUserId;
            }
            
            const response = await fetch('https://celebrated-charisma-production-5701.up.railway.app/review_flashcard', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });
            
            const data = await response.json();
            console.log('Flashcard reviewed:', data);
            
            if (response.ok) {
                // Move to next flashcard
                setCurrentFlashcardIndex(prev => (prev + 1) % flashcards.length);
                setShowFlashcardAnswer(false);
            } else {
                console.log(`Error reviewing flashcard: ${data.detail || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error reviewing flashcard:', error);
            console.log('Error reviewing flashcard: ' + error.message);
        }
    };

    // 10. Get Knowledge Profile
    const fetchKnowledgeProfile = async () => {
        try {
            const response = await fetch('https://celebrated-charisma-production-5701.up.railway.app/knowledge_profile', {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch knowledge profile');
            }
            
            const data = await response.json();
            console.log('Knowledge profile:', data);
            setKnowledgeProfile(data);
        } catch (error) {
            console.error('Error fetching knowledge profile:', error);
        }
    };

    // Flashcard navigation
    const nextFlashcard = () => {
        const flashcardList = flashcardResponse?.flashcards || flashcards;
        setCurrentFlashcardIndex(prev => (prev + 1) % flashcardList.length);
        setShowFlashcardAnswer(false);
    };

    const prevFlashcard = () => {
        const flashcardList = flashcardResponse?.flashcards || flashcards;
        setCurrentFlashcardIndex(prev => (prev - 1 + flashcardList.length) % flashcardList.length);
        setShowFlashcardAnswer(false);
    };

    // Shuffle flashcards
    const shuffleFlashcards = () => {
        if (flashcardResponse && flashcardResponse.flashcards && flashcardResponse.flashcards.length > 0) {
            const shuffled = [...flashcardResponse.flashcards].sort(() => Math.random() - 0.5);
            const newResponse = {
                ...flashcardResponse,
                flashcards: shuffled
            };
            setFlashcardResponse(newResponse);
            setCurrentFlashcardIndex(0);
            setShowFlashcardAnswer(false);
        }
    };

    // Generate new flashcards (moved to shuffle button)
    const generateNewFlashcards = async () => {
        await generateFlashcards();
    };

    // Drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
        setIsDragOverScreen(true);
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
        setIsDragOverScreen(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set to false if we're leaving the drop zone entirely
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragOver(false);
            setIsDragOverScreen(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        setIsDragOverScreen(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const droppedFile = files[0];
            
            // Validate file type
            if (droppedFile.type === 'application/pdf') {
                setFile(droppedFile);
            } else {
                console.log('Please drop a PDF file only.');
            }
        }
    };

    // Remove loading checks - dashboard should always render
    // if (!user) {
    //     return <div>Loading...</div>;
    // } else if (isLoading) {
    //     return <div>Loading...</div>;
    // }

    const userName = user?.displayName || 'User';
    const loadingText = [
        'Did you know that AI can help you study faster and better?',
        '1903 people are studying with us right now',
        'Almost done with your file...',
    ];



    return (
        <div className={`dashboard${showChat ? ' dashboard--chat-mode' : ''}`}>
            {/* Header */}
            <div className="dashboard-header">
                <div className="header-left">
                    <h1 className="dashboard-title">
                        {/* <PanelLeft onClick={toggleSidebar} className="dashboard-title-icon" /> */}
                        
                        Home
                    </h1>

                    <div className="header-left-mobile">
                        <Menu onClick={toggleSidebar} className="header-left-mobile-icon" />
                    </div>
                    
                </div>
                
                <div className="header-right">
                    {/* Secondary Sidebar Toggle Button */}
                    {/* <button 
                        className="right-sidebar-toggle-btn"
                        onClick={toggleRightSidebar}
                        title="Toggle secondary sidebar"
                    >
                        <PanelLeft size={20} />
                    </button> */}
                    
                    <div className="filter-tabs">
                        {/* <button 
                            className={`filter-tab ${activeTab === 'All' ? 'active' : ''}`}
                            onClick={() => setActiveTab('All')}
                        >
                            All
                        </button> */}
                        {/* <button 
                            className={`filter-tab ${activeTab === 'Shared with you' ? 'active' : ''}`}
                            onClick={() => setActiveTab('Shared with you')}
                        >
                            Shared with you
                        </button> */}
                        {/* <button 
                            className={`filter-tab ${activeTab === 'Owned by you' ? 'active' : ''}`}
                            onClick={() => setActiveTab('Owned by you')}
                        >
                            Owned by you
                        </button> */}
                    </div>
                    
             

                    {/* Subscription Status */}
                    {/* {!subscriptionLoading && subscription && (
                        <div className="subscription-status">
                            <div 
                                className={`subscription-badge ${subscription.status === 'active' ? 'active' : 'free'} ${subscription.status !== 'active' ? 'clickable' : ''}`}
                                onClick={handleSettingsClick}
                                style={{ cursor: 'pointer' }}
                            >
                                {subscription.status === 'active' ? (
                                    <>
                                        <Sparkle className="subscription-icon" />
                                        {subscription.plan === 'pro' ? 'Pro' : subscription.plan === 'enterprise' ? 'Enterprise' : 'Active'}
                                    </>
                                ) : (
                                    <>
                                        <WalletCards className="subscription-icon" />
                                        Free Plan
                                    </>
                                )}
                            </div>
                        </div>
                    )} */}
                    
                    <div className="view-toggles">
                        {/* <button 
                            className={`view-toggle ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => setViewMode('grid')}
                        >
                            ⊞
                        </button>
                        <button 
                            className={`view-toggle ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => setViewMode('list')}
                        >
                            ☰
                        </button> */}
                    </div>
                </div>
            </div>

       
            {!showChat && (
            <>
            <div className="section">
                <div className="section-header">
                    {/* <h2 className="section-title">Start Studying Here</h2> */}
                    {/* <div className="count-badge">{documents.length}</div> */}
                </div>
                
                <div className="cards-container">
                    <p className="dashboard-welcome-text">Hi, how can I help you today?</p>

{/* 
                    <Link to="/onboarding">
                    <p>Onboarding page</p>
                    </Link> */}

                    {/* Action Buttons */}
                    <div className="action-buttons-container">
                        {SHOW_QUIZ_FEATURES && (
                        <button 
                            className="action-button quiz-button active" 
                            onClick={() => {
                                handleActionButtonClick('quiz');
                                setShowQuizModal(true);
                            }}
                            disabled={!currentDocumentId}
                        >
                            <div className="button-icon-section">
                                <svg className="button-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                                    <path d="M6 8h2"/>
                                    <path d="M6 12h2"/>
                                    <path d="M16 8h2"/>
                                    <path d="M16 12h2"/>
                                </svg>
                            </div>
                            <div className="button-text-section">
                                <span>Generate Quiz</span>
                            </div>
                        </button>
                        )}

                        <button 
                            className={`action-button quiz-button ${SHOW_QUIZ_FEATURES ? '' : 'active'}`}
                            onClick={handleImportWebLinkClick}
                            // disabled={!currentDocumentId}
                        >
                            <div className="button-icon-section">
                                <svg className="button-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21.44 11.05l-8.49 8.49a5 5 0 01-7.07-7.07l8.49-8.49a3.5 3.5 0 014.95 4.95l-8.49 8.49a2 2 0 01-2.83-2.83l7.78-7.78" />
                                </svg>
                            </div>
                            <div className="button-text-section">
                                <div style={{display: 'flex', flexDirection: 'column', gap: '5px'}}>
                                <span>Import Web link</span>
                                <span style={{fontSize: '10px', color: '#6b7280'}}>Youtube links</span>
                                </div>
                            </div>
                        </button>
                    </div>

                      
                    <div className="section-header">
                    <h2 className="section-title-dashboard">Your Course Materials</h2>
                    {/* <div className="count-badge">{documents.length}</div> */}

                  
                </div>
                    <div className="material-library">
                        <h3 className="material-subheading">PDFs & uploads</h3>
                        <div className={`document-grid folder-grid ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
                            {documents.map((document, index) => {
                                const folderArt = FOLDER_TILE_ART[index % FOLDER_TILE_ART.length];
                                const itemCount = Math.max(1, document.chunk_count || 1);
                                const name = document.document_name || 'Untitled';
                                const docKey = (document.document_name || '').trim().toLowerCase();
                                const linkedNote = docKey ? documentNameToNote.get(docKey) : null;
                                return (
                                    <div key={document.document_id || index} className="folder-grid-item">
                                        <div
                                            className={`folder-tile folder-tile--has-art ${
                                                currentDocumentId === document.document_name ? 'folder-tile--selected' : ''
                                            } ${viewMode === 'list' ? 'folder-tile--list' : ''}`}
                                            onClick={() => setCurrentDocumentId(document.document_name)}
                                            title={document.document_name}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setCurrentDocumentId(document.document_name);
                                                }
                                            }}
                                        >
                                            <div className="folder-tile-art-wrap" aria-hidden>
                                                <img src={folderArt} alt="" className="folder-tile-img" />
                                            </div>
                                            <div className="folder-body">
                                                <span className="folder-count">{itemCount} items</span>
                                                <span className="folder-name">{name}</span>
                                                <span className="folder-meta">
                                                    {new Date(document.upload_date).toLocaleDateString()} ·{' '}
                                                    {Math.round((document.file_size || 0) / 1024)} KB
                                                </span>
                                            </div>
                                            {currentDocumentId === document.document_name && (
                                                <span className="folder-active-badge">Active</span>
                                            )}
                                            <button
                                                type="button"
                                                className="delete-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteDocument(document.document_id || document.id);
                                                }}
                                                title={`Delete "${document.document_name}"`}
                                            >
                                                ×
                                            </button>
                                        </div>
                                        {linkedNote && user?.uid && (
                                            <button
                                                type="button"
                                                className="folder-view-note-link"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/notes/${user.uid}/${linkedNote.id}`);
                                                }}
                                            >
                                                View note
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            <div className="upload-doc-button folder-upload-slot">
                                <div
                                    onClick={() => {
                                        const fileInput = document.querySelector('input[type="file"]');
                                        if (fileInput) fileInput.click();
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            const fileInput = document.querySelector('input[type="file"]');
                                            if (fileInput) fileInput.click();
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <Plus size={22} strokeWidth={2} />
                                    <p>Upload</p>
                                </div>
                            </div>
                        </div>

                        {youtubeNotes.length > 0 && (
                            <>
                                <h3 className="material-subheading">YouTube & saved links</h3>
                                <div className="note-article-list">
                                    {youtubeNotes.map((note) => {
                                        const vid = extractYoutubeVideoId(note.url);
                                        const thumb = vid
                                            ? `https://img.youtube.com/vi/${vid}/mqdefault.jpg`
                                            : null;
                                        return (
                                            <div
                                                key={note.id}
                                                className="note-article-card"
                                                role="link"
                                                tabIndex={0}
                                                onClick={() => navigate(`/notes/${user?.uid}/${note.id}`)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        navigate(`/notes/${user?.uid}/${note.id}`);
                                                    }
                                                }}
                                            >
                                                <div className="note-article-thumb">
                                                    {thumb ? (
                                                        <img src={thumb} alt="" loading="lazy" />
                                                    ) : (
                                                        <span className="note-article-thumb-fallback">▶</span>
                                                    )}
                                                </div>
                                                <div className="note-article-main">
                                                    <div className="note-article-title">{note.title || 'YouTube note'}</div>
                                                    <div className="note-article-preview">{buildNotePreview(note)}</div>
                                                    <div className="note-article-foot">
                                                        Open full page
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                    

                    <div id="upload-card" className="create-card">
                        <FileUpload onFileUpload={(file) => uploadFile(file)} />
                    </div>
                
                
                </div>
            </div>
            </>
            )}

            {/* Action Buttons */}
            {/* <div className="section">
                <div className="section-header">
                    <h2 className="section-title">Learning Tools</h2>
                </div>
                <div className={`cards-container ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
                    <button onClick={() => setShowChat(!showChat)} className={`action-card ${viewMode === 'list' ? 'list-item' : 'grid-item'}`}>
                        <div className="create-icon"><MessageSquare style={{color:  '#111827'}} className="create-icon-book" /></div>
                        <span className="create-text">Ask a Question</span>
                    </button>
                    <button onClick={() => {
                        console.log('Quiz button clicked! Opening sophisticated modal');
                        setShowQuizModal(true);
                    }} className={`action-card ${viewMode === 'list' ? 'list-item' : 'grid-item'}`}>
                        <div className="create-icon"><Sparkles style={{color:  '#111827'}} className="create-icon-book" /></div>
                        <span className="create-text">Generate Quiz</span>
                    </button>
                    <button onClick={() => {
                        console.log('Flashcard button clicked! showFlashcards:', showFlashcards);
                        setShowFlashcards(!showFlashcards);
                    }} className={`action-card ${viewMode === 'list' ? 'list-item' : 'grid-item'}`}>
                        <div className="create-icon"><WalletCards style={{color:  '#111827'}} className="create-icon-book" /></div>
                        <span className="create-text">Study with Flashcards</span>
                    </button>
                    <button onClick={() => setShowKnowledgeProfile(!showKnowledgeProfile)} className="action-card">
                        <div className="create-icon"><Calendar style={{color:  '#111827'}} className="create-icon-book" /></div>
                        <span className="create-text">Create a Study Plan</span>
                    </button>
                </div>
            </div> */}

             {showImportWebLinkModal && (
                 <div className="modal-overlay">
                     <div className="web-link-modal">
                         <div className="web-link-modal-content">
                             <div className="web-link-modal-header">
                                 <h2>Web link</h2>
                                 <button className="web-link-modal-close" onClick={handleCloseImportWebLinkModal}>
                                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                         <polyline points="6,9 12,15 18,9"></polyline>
                                     </svg>
                                     <span className="esc-text">close</span>
                                 </button>
                             </div>

                            <div className="web-link-input-section">
                                <label className="web-link-label">Link</label>
                                <input 
                                    type="text" 
                                    className="web-link-input"
                                    placeholder="https://youtube.com/watch?v=..." 
                                    value={youtubeUrl}
                                    onChange={(e) => setYoutubeUrl(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && !isUploadingYoutube) {
                                            handleSummarizeLink();
                                        }
                                    }}
                                    disabled={isUploadingYoutube}
                                />
                                <p className="web-link-description">
                                    {isUploadingYoutube ? '⏳ Processing video...' : '✨ Works with YouTube links - powered by RapidAPI'}
                                </p>
                            </div>

                            <div className="web-link-actions">
                                <button 
                                    className="web-link-primary-btn"
                                    onClick={handleSummarizeLink}
                                    disabled={isUploadingYoutube || !youtubeUrl.trim()}
                                >
                                    <svg className="web-link-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                    </svg>
                                    {isUploadingYoutube ? '⏳ Processing...' : 'Summarize link'}
                                </button>
                                 
                                 {/* <button className="web-link-secondary-btn">
                                     <svg className="web-link-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                         <polyline points="15,18 9,12 15,6"></polyline>
                                     </svg>
                                     More note options
                                 </button> */}
                             </div>
                         </div>
                     </div>
                 </div>
             )}

            {/* My Quizzes Section */}
            {/* <div className="section">
                <div className="section-header">
                    <h2 className="section-title">My quizzes</h2>
                </div>
                
                <div className="quizzes-card">
                    <div className="quizzes-info">
                        <div className="quizzes-info-content">
                        <div className="info-icon">i</div>
                        <span>Once you create your first quiz, it will appear here.</span>
                        </div>
                    <button 
                        className="quizzes-btn"
                        onClick={() => setShowQuizModal(true)}
                        disabled={!currentDocumentId}
                    >
                        <span className="btn-icon">+</span>
                        Create Quiz
                    </button>
                    </div>
                </div>
            </div> */}

            {/* <div className="section">
                <div className="section-header">
                    <h2 className="section-title">Study with Flashcards</h2>
                </div>
                
                <div className="quizzes-card">

                   
                    <div className="quizzes-info">
                    <div className="quizzes-info-content">
                        <div className="info-icon">i</div>
                        <span>Once you create your first flashcard, it will appear here.</span>
                        </div>
                    <button 
                        className="quizzes-btn"
                            onClick={() => setShowFlashcards(true)}
                        disabled={!currentDocumentId}
                    >
                        <span className="btn-icon">+</span>
                        Create Flashcard
                    </button>
                    </div>
                </div>
            </div> */}

            {!showChat && (
            <div
                className={`dashboard-quick-chat-dock ${isRightSidebarCollapsed ? 'dashboard-quick-chat-dock--rs-narrow' : 'dashboard-quick-chat-dock--rs-wide'}`}
                aria-label="Quick question"
            >
                <div className="quick-chat-container">
                    <div className="quick-chat-input-container">
                        <input
                            type="text"
                            placeholder="Ask anything, or drag and drop your PDF files here..."
                            value={quickChatInput}
                            onChange={(e) => setQuickChatInput(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleQuickChat();
                                }
                            }}
                            className="quick-chat-input"
                        />
                        <button
                            type="button"
                            className="quick-chat-send-btn"
                            onClick={handleQuickChat}
                            disabled={!quickChatInput.trim() || !currentDocumentId}
                        >
                            <span className="send-arrow">→</span>
                        </button>
                    </div>
                    <div className="quick-chat-actions">
                        <button type="button" className="quick-chat-action-btn">
                            <Folder size={16} />
                            All Files
                        </button>
                        <button type="button" className="quick-chat-action-btn">
                            <span className="upload-icon">📎</span>
                            Upload
                        </button>
                    </div>
                </div>
            </div>
            )}

            {/* Chat — full-page studio layout (brand accent, no modal card) */}
            {showChat && (
                <div className="dashboard-chat-fullscreen dashboard-chat-studio">
                    <div className="studio-chat-shell" onClick={(e) => e.stopPropagation()}>
                        <header className="studio-chat-topbar">
                            <button
                                type="button"
                                className="studio-chat-back"
                                onClick={() => setShowChat(false)}
                            >
                                ← Back
                            </button>
                        </header>

                        <div className="studio-chat-body">
                            <div className="studio-chat-scroll">
                                {chatMessages.length === 0 && !chatLoading && (
                                    <div className="studio-chat-empty">
                                        <p className="studio-chat-greeting">
                                            Hey {studioChatFirstName}! What would you like to learn today?
                                        </p>
                                        <Sparkles className="studio-chat-greeting-mark" size={28} aria-hidden />
                                        {!currentDocumentId && (
                                            <p className="studio-chat-hint">
                                                Select a document from Your Course Materials (back) so answers can use your files.
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className="studio-chat-thread">
                                    {chatMessages.map((message, index) => {
                                        const isUser = message.startsWith('You:');
                                        const messageContent = message.replace(/^(You:|AI:)\s*/, '');
                                        const isTrailingAi =
                                            message.startsWith('AI:') && index === chatMessages.length - 1;

                                        if (isUser) {
                                            return (
                                                <div key={index} className="studio-msg studio-msg--user">
                                                    <div className="studio-msg-pill">{messageContent}</div>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div key={index} className="studio-msg studio-msg--ai">
                                                <div className="studio-msg-ai-inner">
                                                    <div className="studio-msg-text studio-msg-text--ai">{messageContent}</div>
                                                    <div className="studio-msg-footer">
                                                        <div className="studio-msg-actions">
                                                            <button
                                                                type="button"
                                                                className="studio-msg-action"
                                                                title="Copy"
                                                                aria-label="Copy response"
                                                                onClick={() => copyStudioText(messageContent)}
                                                            >
                                                                <Copy size={16} strokeWidth={1.75} />
                                                            </button>
                                                            <button type="button" className="studio-msg-action" title="Helpful" aria-label="Mark helpful">
                                                                <ThumbsUp size={16} strokeWidth={1.75} />
                                                            </button>
                                                            <button type="button" className="studio-msg-action" title="Not helpful" aria-label="Mark not helpful">
                                                                <ThumbsDown size={16} strokeWidth={1.75} />
                                                            </button>
                                                            {isTrailingAi && (
                                                                <button
                                                                    type="button"
                                                                    className="studio-msg-action"
                                                                    title="Regenerate"
                                                                    aria-label="Regenerate response"
                                                                    disabled={chatLoading}
                                                                    onClick={() => handleStudioRegenerate(index)}
                                                                >
                                                                    <RefreshCw size={16} strokeWidth={1.75} />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <Sparkles className="studio-msg-mark" size={22} aria-hidden />
                                                    </div>
                                                </div>
                                                {isTrailingAi && (
                                                    <FollowUpQuestions
                                                        selectedDocument={currentDocumentId ? {
                                                            id: currentDocumentId,
                                                            document_id: currentDocumentId,
                                                            title: currentDocumentId,
                                                            name: currentDocumentId
                                                        } : null}
                                                        user={user}
                                                        showAfterDelay={500}
                                                        className="follow-up-questions-in-chat studio-follow-up-questions"
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}

                                    {chatLoading && (
                                        <div className="studio-msg studio-msg--ai studio-msg--typing">
                                            <div className="studio-typing">
                                                <span className="studio-typing-dot" />
                                                <span className="studio-typing-dot" />
                                                <span className="studio-typing-dot" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="studio-chat-composer-outer">
                            <div className="studio-chat-composer">
                                <button
                                    type="button"
                                    className="studio-chat-composer-icon"
                                    title="Return to dashboard to upload or switch documents"
                                    aria-label="Return to dashboard to upload or switch documents"
                                    onClick={() => setShowChat(false)}
                                >
                                    <Plus size={20} strokeWidth={2} />
                                </button>
                                <input
                                    type="text"
                                    className="studio-chat-composer-input"
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    placeholder="Reply…"
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
                                        e.preventDefault();
                                        void handleChat();
                                    }}
                                />
                                <div className="studio-chat-composer-meta">
                                    <span className="studio-chat-doc-chip" title={currentDocumentId || undefined}>
                                        <ChevronDown size={14} aria-hidden />
                                        {studioActiveDocLabel}
                                    </span>
                                    <button
                                        type="button"
                                        className="studio-chat-send"
                                        onClick={() => void handleChat()}
                                        disabled={chatLoading || !chatInput.trim()}
                                        aria-label="Send message"
                                    >
                                        <ArrowUp size={18} strokeWidth={2.25} />
                                    </button>
                                </div>
                            </div>
                            <p className="studio-chat-disclaimer">
                                AI can make mistakes. Please double-check answers, especially for exams or grades.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Sophisticated Quiz Creation Modal */}
            {SHOW_QUIZ_FEATURES && showQuizModal && (
                <div className="quiz-modal-overlay">
                    <div className="quiz-modal">
                        <div className="quiz-modal-header">
                            <h2>Tell us a bit more about how you want it?</h2>
                            <div className="dashboard-progress-bar">
                                <div className="progress-step active">1</div>
                                <div className="progress-step active">2</div>
                                <div className="progress-step">3</div>
                            </div>
                            <button className="close-button" onClick={() => setShowQuizModal(false)}>×</button>
                        </div>
                        
                        <div className="quiz-modal-content">
                            {/* Quiz Length */}
                            <div className="quiz-option-section">
                                <h3>Quiz Length</h3>
                                <div className="option-grid">
                                    {[
                                        { value: 'short', label: 'Short', range: '1-10 questions' },
                                        { value: 'medium', label: 'Medium', range: '1-35 questions' },
                                        { value: 'long', label: 'Long', range: '1-50 questions' }
                                    ].map(option => (
                                        <div 
                                            key={option.value}
                                            className={`option-card ${quizOptions.length === option.value ? 'selected' : ''}`}
                                            onClick={() => setQuizOptions(prev => ({ ...prev, length: option.value }))}
                                        >
                                            {/* <div className="option-icon">⏱️</div> */}
                                            <div className="option-label">{option.label}</div>
                                            <div className="option-range">{option.range}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Difficulty Level */}
                            <div className="quiz-option-section">
                                <h3>What Level?</h3>
                                <div className="option-grid">
                                    {[
                                        { value: 'beginner', label: 'Beginner', icon: '😊' },
                                        { value: 'intermediate', label: 'Intermediate', icon: '📖' },
                                        { value: 'advanced', label: 'Advanced', icon: '🏆' }
                                    ].map(option => (
                                        <div 
                                            key={option.value}
                                            className={`option-card ${quizOptions.difficulty === option.value ? 'selected' : ''}`}
                                            onClick={() => setQuizOptions(prev => ({ ...prev, difficulty: option.value }))}
                                        >
                                            {/* <div className="option-icon">{option.icon}</div> */}
                                            <div className="option-label">{option.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Style */}
                            <div className="quiz-option-section">
                                <h3>What style you prefer?</h3>
                                <div className="option-grid">
                                    {[
                                        { value: 'genz', label: 'Gen Z', icon: '😄' },
                                        { value: 'scholar', label: 'Scholar', icon: '🎓' },
                                        { value: 'engaging', label: 'Engaging', icon: '⚡' }
                                    ].map(option => (
                                        <div 
                                            key={option.value}
                                            className={`option-card ${quizOptions.style === option.value ? 'selected' : ''}`}
                                            onClick={() => setQuizOptions(prev => ({ ...prev, style: option.value }))}
                                        >
                                            {/* <div className="option-icon">{option.icon}</div> */}
                                            <div className="option-label">{option.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Question Types */}
                            <div className="quiz-option-section">
                                <h3>What types of questions would you like?</h3>
                                <div className="option-grid">
                                    {[
                                        { value: 'true_false', label: 'True/False', icon: '✅' },
                                        { value: 'multiple_choice', label: 'Multiple Choice', icon: '☑️' },
                                        { value: 'fill_blank', label: 'Fill in the Blanks', icon: '✏️' },
                                        { value: 'match_pairs', label: 'Match Pairs', icon: '↔️' }
                                    ].map(option => (
                                        <div 
                                            key={option.value}
                                            className={`option-card ${quizOptions.questionTypes.includes(option.value) ? 'selected' : ''}`}
                                            onClick={() => {
                                                setQuizOptions(prev => {
                                                    const newTypes = prev.questionTypes.includes(option.value)
                                                        ? prev.questionTypes.filter(t => t !== option.value)
                                                        : [...prev.questionTypes, option.value];
                                                    return { ...prev, questionTypes: newTypes };
                                                });
                                            }}
                                        >
                                            {/* <div className="option-icon">{option.icon}</div> */}
                                            <div className="option-label">{option.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="quiz-modal-footer">
                            <div className="file-info">1 File</div>
                            <button 
                                className="continue-button"
                                onClick={() => {
                                    setShowQuizModal(false);
                                    setShowQuiz(true);
                                    generateQuiz();
                                }}
                                disabled={quizOptions.questionTypes.length === 0}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Quiz Interface */}
            {SHOW_QUIZ_FEATURES && showQuiz && (
                <div className="modal-overlay" onClick={() => setShowQuiz(false)}>
                    <div className={`quiz-container ${isQuizExpanded ? 'expanded' : ''}`} onClick={(e) => e.stopPropagation()}>
                        <div className="quiz-header">
                            <h1>Quiz</h1>
                            <div className="modal-controls">
                                <button 
                                    className="expand-button" 
                                    onClick={() => setIsQuizExpanded(!isQuizExpanded)}
                                    title={isQuizExpanded ? 'Minimize' : 'Expand'}
                                >
                                    {isQuizExpanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                                </button>
                                <button className="close-button" onClick={() => setShowQuiz(false)}>×</button>
                            </div>
                        </div>

                    <div className='quiz-header-row'>
                    <p onClick={generateQuiz} className="generate-quiz-btn">generate quiz</p>

                    {/* <div>
                        <button className="regenerate-button" onClick={generateQuiz} disabled={quizLoading}>
                            <RefreshCcw className="regenerate-icon" />
                            {quizLoading ? 'Generating...' : 'Regenerate'}
                        </button>
                    </div> */}

                    {/* <div>
                        <button className="timer-button" onClick={() => setTimer(!timer)}>
                            <Clock className="timer-icon" />
                            Timer (optional)
                        </button>
                    </div> */}
                    </div>

                    {/* Quiz Information */}
                    {quizResponse && (
                        <div className="quiz-info">
                            <div className="quiz-source">
                                Active quiz from: {quizResponse.document_name || 'Generated Document'}
                            </div>
                            <div className="quiz-created">
                                Created: {new Date().toLocaleDateString('en-GB')}
                            </div>
                        </div>
                    )}

                    {/* Quiz Content */}
                    <div className="quiz-content-2">
                        {quizLoading ? (
                            <div className="quiz-skeleton">
                                <div className="skeleton-header">
                                    <div className="skeleton-line skeleton-title"></div>
                                </div>
                                <div className="skeleton-questions">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="skeleton-question">
                                            <div className="skeleton-line skeleton-question-text"></div>
                                            <div className="skeleton-options">
                                                <div className="skeleton-line skeleton-option"></div>
                                                <div className="skeleton-line skeleton-option"></div>
                                                <div className="skeleton-line skeleton-option"></div>
                                                <div className="skeleton-line skeleton-option"></div>
                                            </div>
                                            <div className="skeleton-line skeleton-explanation"></div>
                                        </div>
                                    ))}
                                </div>
                                <div className="skeleton-loading">
                                    <div className="loading-spinner"></div>
                                    <p>Generating your quiz...</p>
                                </div>
                            </div>
                        ) : quizResponse ? (
                            <div className="quiz-questions">
                                <h3>Generated Quiz</h3>
                                {quizResponse.questions && quizResponse.questions.map((question, index) => (
                                    <div key={index} className="question-card">
                                        <h4>Question {index + 1}: {question.question}</h4>
                                        <div className="options">
                                            {question.options && question.options.map((option, optIndex) => (
                                                <div key={optIndex} className="option">
                                                    {String.fromCharCode(65 + optIndex)}. {option}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : quizzes.length > 0 ? (
                            <div className="quiz-questions">
                                {quizzes.map((quiz, quizIndex) => (
                                    <div key={quiz.id} className="quiz-set">
                                        <h4>Active quiz from: {quiz.document_name}</h4>
                                        <p className="quiz-created">Created: {new Date(quiz.created_at).toLocaleDateString()}</p>
                                        {quiz.questions && quiz.questions.map((question, questionIndex) => (
                                            <div key={question.id} className="question-card">
                                                <h5>Question {questionIndex + 1}: {question.question}</h5>
                                                {question.is_raw_content ? (
                                                    <div className="raw-content">
                                                        <pre style={{whiteSpace: 'pre-wrap', fontSize: '14px', background: '#f5f5f5', padding: '10px', borderRadius: '4px'}}>
                                                            {cleanRawQuizContent(question.explanation)}
                                                        </pre>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="options">
                                                            {question.options && question.options.map((option, optIndex) => (
                                                                <div key={optIndex} className="option">
                                                                    {String.fromCharCode(65 + optIndex)}. {option}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="quiz-generator">
                                <p>Click "Generate Quiz" to create a quiz from your selected document.</p>
                                <button onClick={generateQuiz} disabled={quizLoading || !currentDocumentId}>
                                    {quizLoading ? 'Generating...' : 'Generate Quiz'}
                                </button>
                            </div>
                        )}
                    </div>
                    {SHOW_QUIZ_FEATURES && (
                    <div 
                        onClick={() => {
                            const userId = localStorage.getItem('rag_user_id') || currentUserId;
                            const quizId = quizResponse?.quiz_id || (quizzes.length > 0 ? quizzes[0].id : null);
                            if (userId && quizId) {
                                window.open(`/take-quiz/${userId}/${quizId}`, '_blank');
                            } else {
                                alert('Please generate a quiz first.');
                            }
                        }} 
                        className='take-quiz-btn'
                    >
                            <Check className="take-quiz-icon" />
                            <p>Take Quiz</p>
                        </div>
                    )}
                    </div>
                </div>
            )}

            {/* Flashcard Interface */}
            {showFlashcards && (
                <div className="flashcard-modal-overlay" onClick={() => setShowFlashcards(false)}>
                    <div className={`flashcard-modal-container ${isFlashcardExpanded ? 'expanded' : ''}`} onClick={(e) => e.stopPropagation()}>
                        <div className="flashcard-modal-header">
                            <h1>Flashcards</h1>
                            <div className="flashcard-modal-controls">
                                <button 
                                    className="flashcard-shuffle-btn" 
                                    onClick={flashcardResponse && flashcardResponse.flashcards && flashcardResponse.flashcards.length > 0 ? shuffleFlashcards : generateNewFlashcards}
                                    title={flashcardResponse && flashcardResponse.flashcards && flashcardResponse.flashcards.length > 0 ? 'Shuffle Cards' : 'Generate Flashcards'}
                                    disabled={flashcardLoading || !currentDocumentId}
                                >
                                    <RefreshCcw size={16} />
                                </button>
                                <button 
                                    className="flashcard-expand-btn" 
                                    onClick={() => setIsFlashcardExpanded(!isFlashcardExpanded)}
                                    title={isFlashcardExpanded ? 'Minimize' : 'Expand'}
                                >
                                    {isFlashcardExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                                </button>
                                <button className="flashcard-close-btn" onClick={() => setShowFlashcards(false)}><X className='flashcard-close-btn-icon' /></button>
                            </div>
                        </div>

                        {flashcardResponse && flashcardResponse.flashcards && flashcardResponse.flashcards.length > 0 && (
                            <div className='flashcard-source-info'>
                                <p>current flashcards from: {currentDocumentId}</p>
                            </div>
                        )}
                        <div className="flashcard-modal-content">
                            {flashcardLoading ? (
                                <div className="flashcard-loading-state">
                                    <div className="flashcard-loading-spinner"></div>
                                    <p>Generating flashcards...</p>
                                </div>
                            ) : flashcardResponse && flashcardResponse.flashcards && flashcardResponse.flashcards.length > 0 ? (
                                <div className="flashcard-study-area">
                                    <div className="flashcard-navigation">
                                        <button 
                                            className="flashcard-nav-btn prev-btn" 
                                            onClick={prevFlashcard} 
                                            disabled={currentFlashcardIndex === 0}
                                        >
                                            ←
                                        </button>
                                        <span className="flashcard-counter">
                                            {currentFlashcardIndex + 1} / {flashcardResponse.flashcards.length}
                                        </span>
                                        <button 
                                            className="flashcard-nav-btn next-btn" 
                                            onClick={nextFlashcard} 
                                            disabled={currentFlashcardIndex === flashcardResponse.flashcards.length - 1}
                                        >
                                            →
                                        </button>
                                    </div>
                                    
                                    <div className="flashcard-display-area">
                                        <div 
                                            className={`flashcard-card ${showFlashcardAnswer ? 'flipped' : ''}`}
                                            onClick={() => setShowFlashcardAnswer(!showFlashcardAnswer)}
                                        >
                                            <div className="flashcard-card-inner">
                                                <div className="flashcard-card-front">
                                                    <div className="flashcard-text">
                                                        <p>{flashcardResponse.flashcards[currentFlashcardIndex]?.front}</p>
                                                    </div>
                                                </div>
                                                <div className="flashcard-card-back">
                                                    <div className="flashcard-text">
                                                        <p>{flashcardResponse.flashcards[currentFlashcardIndex]?.back}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="flashcard-actions-area">
                                        {!showFlashcardAnswer ? (
                                            <button onClick={() => setShowFlashcardAnswer(true)} className="show-answer-button">
                                                SHOW ANSWER
                                            </button>
                                        ) : (
                                            <div className="difficulty-feedback">
                                                <p>How difficult was this?</p>
                                                <div className="difficulty-buttons">
                                                    <button onClick={() => reviewFlashcard(flashcardResponse.flashcards[currentFlashcardIndex].id, 0.2)} className="difficulty-btn easy">
                                                        Easy
                                                    </button>
                                                    <button onClick={() => reviewFlashcard(flashcardResponse.flashcards[currentFlashcardIndex].id, 0.5)} className="difficulty-btn medium">
                                                        Medium
                                                    </button>
                                                    <button onClick={() => reviewFlashcard(flashcardResponse.flashcards[currentFlashcardIndex].id, 0.8)} className="difficulty-btn hard">
                                                        Hard
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="flashcard-empty-state">
                                    <div className="flashcard-generate-prompt">
                                        <p>Click the shuffle button above to generate flashcards from your selected document.</p>

                                        <div className="create-flashcards-btn" onClick={() => setShowFlashcards(true)} disabled={!currentDocumentId}>
                                            <p>+ Create Flashcards</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Knowledge Profile Interface */}
            {showKnowledgeProfile && (
                <div className="knowledge-container">
                    <div className="knowledge-header">
                        <h1>Knowledge Profile</h1>
                        <button className="close-button" onClick={() => setShowKnowledgeProfile(false)}>×</button>
                    </div>
                    <div className="knowledge-content">
                        {knowledgeProfile ? (
                            <div className="knowledge-stats">
                                <div className="stat-card">
                                    <h3>Documents</h3>
                                    <p>{knowledgeProfile.stats.total_documents}</p>
                                </div>
                                <div className="stat-card">
                                    <h3>Chats</h3>
                                    <p>{knowledgeProfile.stats.total_chats}</p>
                                </div>
                                {SHOW_QUIZ_FEATURES && (
                                <div className="stat-card">
                                    <h3>Quizzes</h3>
                                    <p>{knowledgeProfile.stats.total_quizzes}</p>
                                </div>
                                )}
                                <div className="stat-card">
                                    <h3>Flashcards</h3>
                                    <p>{knowledgeProfile.stats.total_flashcards}</p>
                                </div>
                                <div className="recent-activity">
                                    <h3>Recent Documents</h3>
                                    {knowledgeProfile.recent_documents && knowledgeProfile.recent_documents.map((doc, index) => (
                                        <div key={index} className="recent-item">
                                            <span>{doc.title}</span>
                                            <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p>Loading knowledge profile...</p>
                        )}
                    </div>
                </div>
            )}

            {/* Success Tip */}
            {successTip && (
                <div className="success-tip">
                    <div className="success-tip-content">
                        <Check className="success-tip-icon" />
                        <h1>{successText}</h1>
                    </div>
                </div>
            )}

            {/* Loading Modal */}
            {loadingModal && (
                <div className="loading-modal">
                    <div className="loading-modal-content">
                        <h1>{loadingText[Math.floor(Math.random() * loadingText.length)]}</h1>
                        <div className="loading-modal-spinner"></div>
                    </div>
                </div>
            )}

            {/* Full-screen drag overlay */}
            {isDragOverScreen && (
                <div className="drag-overlay">
                    <div className="drag-overlay-content">
                        <div className="drag-overlay-icon"><Folder size={60} className="drag-overlay-icon-icon" /></div>
                        <h2 className="drag-overlay-title">Drop Here</h2>
                        <p className="drag-overlay-subtitle">Release to upload your PDF</p>
                    </div>
                </div>
            )}

            {/* Pricing Modal */}
            {showPricingModal && (
                <PricingModal 
                    onClose={() => setShowPricingModal(false)}
                    user={user}
                />
            )}

            {/* Settings Modal */}
            {showSettingsModal && (
                <SettingsModal 
                    onClose={() => setShowSettingsModal(false)}
                    user={user}
                />
            )}

            {/* Upgrade Modal */}
            <UpgradeModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                feature={upgradeModalData.feature}
                currentUsage={upgradeModalData.currentUsage}
                limit={upgradeModalData.limit}
            />
        </div>
    );
});

export default Dashboard;

